import React, { DragEvent, isValidElement, MouseEventHandler, useContext, useEffect, useMemo, useRef } from 'react';

import cl from 'classnames';
import { isEmpty } from 'ramda';
import { useDragLayer, useDrop } from 'react-dnd';

import ActionsMenu from 'components/Pages/Admin/Itinerary/Details/ActionsMenu';
import Icon from 'components/shared/Icon';
import Plus from 'components/shared/Icon/icons/Plus';
import Text from 'components/shared/Typography/Text';
import { getCurrentDateString } from 'utils/date';
import { debounce } from 'utils/debounce';

import { EOrganizedBy } from '../../../../../../../../../../detailsv2/structure';
import { useAddEventMenuActions } from '../../../../functions';
import { DragAndDropContext } from '../../contexts';
import { DragTypeEnum } from '../../contexts/drag-and-drop';
import { getDayNumberFromCursor } from '../../utils';
import daysGenerator from '../../utils/days-generator';
import * as constants from './constants';
import s from './index.module.css';
import { CalendarEvent, Props, TCalendarDay } from './types';
import { checkIsHoverInitial, parseAndFormatDate } from './utils';

function CalendarDay({
  calendarDay,
  children,
  containerStyles = {},
  onSizeChange,
  active,
  isHighlighted,
  availableDayForPlus,
  updateSelectedPeriod,
  organizedBy,
}: Props) {
  const { onHover, selectedEvents, updateSelectedEvents, setTempCalendarEvents } = useContext(
    DragAndDropContext.context,
  );
  const isEmptyDay = isValidElement(children) && isEmpty(children?.props?.children);
  const addEventMenuActions = useAddEventMenuActions({
    dayNumber: calendarDay.dayNumber || 1,
    dayDate: calendarDay.date || (organizedBy === EOrganizedBy.Date ? getCurrentDateString() : null),
  });
  const dragLayer = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
  }));

  const calculatedStyles = useMemo(
    () => ({ zIndex: daysGenerator.NUMBER_OF_DAYS - calendarDay.dayNumber }),
    [calendarDay],
  );

  const dayDate = useMemo(() => {
    if (!calendarDay.date) return null;

    return parseAndFormatDate(calendarDay.date);
  }, [calendarDay.date]);

  const resetCheckIsHoverInitialClosure = () => checkIsHoverInitial({ dayNumber: -1 });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, drop] = useDrop<Pick<CalendarEvent, 'id'>, TCalendarDay, { isOver: boolean; endDrop: any }>(
    () => ({
      accept: [constants.dragAndDrop.EVENT_PLANK_DEFAULT, constants.dragAndDrop.EVENT_PLANK_EXTENDS],
      drop: () => {
        resetCheckIsHoverInitialClosure();
        return calendarDay;
      },
      hover: (item, monitor) => {
        const itemType = monitor.getItemType();
        const isOver = monitor.isOver();

        if (!isOver) return;
        if (!checkIsHoverInitial(calendarDay)) return false;

        const dragType = (() => {
          if (itemType === constants.dragAndDrop.EVENT_PLANK_EXTENDS) return DragTypeEnum.extend;
          return DragTypeEnum.move;
        })();

        onHover({ id: item.id }, { dayNumber: calendarDay.dayNumber }, dragType);
      },
    }),
    [calendarDay, selectedEvents],
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const prevClient = useRef<{ height: number }>();

  const handleDayClick = () => {
    updateSelectedEvents([]);
  };

  const handleDayTitleClick: MouseEventHandler<HTMLDivElement> = (e) => {
    if (e.shiftKey) {
      e.stopPropagation();
      updateSelectedPeriod(calendarDay.dayNumber);
      return;
    }
  };

  const handleDragLeave = debounce((e: DragEvent<HTMLDivElement>) => {
    const num = getDayNumberFromCursor(e.clientX, e.clientY);
    if (!num) {
      resetCheckIsHoverInitialClosure();
      setTempCalendarEvents([]);
    }
  }, 200);

  useEffect(() => {
    if (containerRef.current) {
      if (prevClient.current?.height !== containerRef.current.clientHeight) {
        if (onSizeChange) onSizeChange({ height: containerRef.current.clientHeight + 50 });

        prevClient.current = { height: containerRef.current.clientHeight };
      }
    }
  }, [children, onSizeChange]);

  return (
    <div
      ref={drop}
      key={calendarDay.dayNumber}
      className={cl(s['day-container'], {
        [s['day-container-active']]: active || (isHighlighted && dragLayer.isDragging),
      })}
      style={{ ...calculatedStyles, ...containerStyles }}
      data-calendar-day-number={calendarDay.dayNumber}
      onClick={handleDayClick}
      onDragLeave={handleDragLeave}
    >
      <ActionsMenu actions={addEventMenuActions} isOpenInClickPlace>
        <div className={s['day-title']} onClick={handleDayTitleClick}>
          <Text isStrong size="medium" className={s['text-day-number']}>
            Day {calendarDay.dayNumber}
          </Text>

          {dayDate && (
            <Text isDisabled size="small" className={cl(s['text-truncate'], s['text-date'])}>
              <span>{dayDate}</span>
            </Text>
          )}
        </div>

        {isEmptyDay && !dragLayer.isDragging && availableDayForPlus === calendarDay.dayNumber && (
          <div className={s.addEvent}>
            <Icon component={() => <Plus fill="#3f3c43" />} className={s.addEventIcon} />
          </div>
        )}

        <div className={s.childrenWrapper} ref={containerRef}>
          {children}
        </div>
      </ActionsMenu>
    </div>
  );
}

export default CalendarDay;
